import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { RootState } from 'src/redux/rootReducerType';
import type { UHMember } from 'src/types/Certification';

/**
 * Returns a query-string suffix (`?as_user_id=<id>`) to append to Account Hub
 * API calls when the hub is viewing a different user than the logged-in one.
 * Returns an empty string when viewing your own data.
 */
export function useAsUserParam(): string {
  const { userId } = useParams<{ userId: string }>();
  const currentUserId = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.id
  );

  if (!userId || !currentUserId) {
    return '';
  }
  const targetId = Number(userId);
  if (targetId === currentUserId) {
    return '';
  }
  return `?as_user_id=${targetId}`;
}

/** Minimal shape needed by consumers that display the proxied member. */
export type ProxyTargetMember = Pick<UHMember, 'first_name' | 'last_name'>;

/**
 * The household member being proxied, or undefined when viewing your own data
 * (or when the target can't be resolved). Resolved from `userHouseholds` — the
 * same source the Account Hub router uses for proxy scope/access, and where the
 * household member list actually lives. Shared by ProxyBanner and ContactInfoPage.
 */
export function useProxyTargetMember(): ProxyTargetMember | undefined {
  const { userId } = useParams<{ userId: string }>();
  const currentUserId = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.id
  );
  const userHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.userHouseholds
  );

  return useMemo(() => {
    if (!userId || !currentUserId) {
      return undefined;
    }
    const targetId = Number(userId);
    if (targetId === currentUserId) {
      return undefined;
    }
    for (const uh of userHouseholds ?? []) {
      const found = uh.members.find((m) => m.user_id === targetId);
      if (found) {
        return found;
      }
    }
    return undefined;
  }, [userId, currentUserId, userHouseholds]);
}
